import { useEffect, useState } from 'react';
import { Platform, StyleSheet, TouchableOpacity, TouchableOpacityProps, View } from 'react-native';
import Animated, { Easing, ReduceMotion, useAnimatedProps, useSharedValue, withTiming } from 'react-native-reanimated';
import Svg, { Circle } from 'react-native-svg';

import { getLightRatingColor } from '@/utils/getLightRatingColor';

interface CircularProgressButtonProps extends TouchableOpacityProps {
  size: number;
  borderColor?: string;
  borderWidth?: number;
  value: number;
}

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const CircularProgressButton = ({ children, size, borderWidth = 8, value, ...props }: CircularProgressButtonProps) => {
  // Circle calculations
  const strokeWidth = size * 0.1;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Clamp the value between 0 and 10
  const clampedValue = Math.max(0, Math.min(value, 10));
  const percentage = clampedValue / 10; // Convert to percentage (0-1)

  const styles = useStyle({ size });

  const isWeb = Platform.OS === 'web';

  // Initial animation values
  const progress = useSharedValue(0);
  const animatedStrokeColor = useSharedValue('black');

  // On web, reanimated's `animatedProps` is not applied to SVG attributes, so we
  // animate the stroke progress/color with CSS transitions instead.
  const [webOffset, setWebOffset] = useState(circumference);
  const [webColor, setWebColor] = useState('black');

  useEffect(() => {
    if (isWeb) {
      // Update after mount so the browser animates from the initial state
      // to the target state via the CSS transition.
      setWebOffset(circumference * (1 - percentage));
      setWebColor(getLightRatingColor(value));
    } else {
      // Animate the progress value to match the percentage with the circumference
      progress.value = withTiming(percentage * circumference, {
        duration: 750,
        easing: Easing.linear,
        reduceMotion: ReduceMotion.System
      });

      // Animate the stroke color
      animatedStrokeColor.value = withTiming(getLightRatingColor(value), {
        duration: 750,
        easing: Easing.linear,
        reduceMotion: ReduceMotion.System
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Calculate the border filling
  const animatedProps = useAnimatedProps(() => {
    return {
      strokeDashoffset: circumference - progress.value,
      stroke: animatedStrokeColor.value
    };
  });

  const baseCircleProps = {
    cx: size / 2,
    cy: size / 2,
    r: radius,
    strokeWidth,
    strokeDasharray: `${circumference} ${circumference}`,
    transform: `rotate(-90 ${size / 2} ${size / 2})` // Rotate the circle to start from the top
  };

  return (
    <TouchableOpacity {...props}>
      <View style={styles.container}>
        <Svg width={size} height={size}>
          <Circle {...baseCircleProps} stroke="black" fill="none" />
          {isWeb ? (
            <Circle
              {...baseCircleProps}
              stroke={webColor}
              strokeDashoffset={webOffset}
              fill="rgba(0, 0, 0, 0.7)"
              {...({ style: { transition: 'stroke 0.75s linear, stroke-dashoffset 0.75s linear' } } as object)}
            />
          ) : (
            <AnimatedCircle {...baseCircleProps} animatedProps={animatedProps} fill="rgba(0, 0, 0, 0.7)" />
          )}
        </Svg>
        {children}
      </View>
    </TouchableOpacity>
  );
};

const useStyle = ({ size }: { size: number }) => {
  const styles = StyleSheet.create({
    container: {
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
      width: size,
      height: size,
      borderRadius: size / 2
    }
  });

  return styles;
};

export default CircularProgressButton;
